<template>
    <div class="user-modal-register-confirmation-email-sent-view px-2">
        <div class="mb-5 text-center">
            <p class="font-weight-bold font-size-200">{{ translate('Check your email inbox now') }}</p>
        </div>

        <div
            class="text-center"
            :class="{
                'mb-5': !shouldShowEducational,
                'mb-4': shouldShowEducational,
            }"
        >
            <div class="position-relative d-inline-block lh-1 mb-3">
                <i class="far fa-envelope" style="font-size: 75px; color: #4285f4"></i>
                <i
                    class="fa fa-check-circle position-absolute"
                    style="
                        bottom: 1px;
                        right: -13px;
                        font-size: 31px;
                        color: #00c851;
                        background: whitesmoke;
                        border-radius: 50%;
                        padding-top: 2px;
                        padding-right: 2px;
                        padding-left: 2px;
                    "
                ></i>
            </div>
            <p class="mb-0" v-html="mainText"></p>
        </div>

        <div
            v-if="shouldShowEducational"
            class="d-flex align-items-center justify-content-between alert bg-education text-white mb-5 gap-75"
        >
            <div class="d-inline-flex align-items-center">
                <i class="far fa-graduation-cap font-size-200"></i>
            </div>
            <div v-html="educationText"></div>
        </div>

        <ButtonWithLoadingState
            data-user-modal-button-submit
            class="btn btn-success btn-lg btn-block font-weight-bold text-uppercase rounded mb-4"
            icon="fa fa-arrow-right lh-1"
            :text="translate('Continue')"
            :is-state-loading="isSubmitting"
            :show-icon-after-text="true"
            @click.stop.prevent="doContinue"
        ></ButtonWithLoadingState>

        <div class="text-center">
            {{ translate("Didn't recieve an email?") }}
            <a
                data-user-modal-link-resend-email
                href="#"
                @click.stop.prevent="resendConfirmation"
                :class="{ 'link-inactive text-muted': disableResend }"
                >{{ translate('Re-send verification email') }}</a
            >
        </div>

        <hr class="mb-4 mt-4" />

        <div class="text-center mt-2">
            {{ translate('Wrong email address?') }}
            <a data-user-modal-link-change-email href="#" @click.stop.prevent="goToDashboard">{{
                translate('Change email address')
            }}</a>
        </div>

        <div class="text-center mt-2">
            {{ translate('Need help?') }}
            <a data-user-modal-link-help href="#" @click.stop.prevent="goToHelpPage">{{ translate('Helpdesk') }}</a>
        </div>
    </div>
</template>

<script>
import ButtonWithLoadingState from '../../../../../qaamgo/modules/sat/components/form/ButtonWithLoadingState';
import TransFilter from '../../../../../qaamgo/modules/sat/components/mixins/TransFilter';
import { projectHelper } from '../../../../misc/project-helper';
import { translate } from '../../../../../qaamgo/helper/translate';
import { pageStoreMixin } from '../../../../store/page-store';
import { satGlobals } from '../../../../../qaamgo/sat/helper/sat-globals';
import { translatePath } from '../../../../../qaamgo/sat/helper/translate-path';
import { userManager } from '../../../user-manager';
import { pageNotification } from '../../../../../qaamgo/helper/notify';
import { globalLogger } from '../../../../../qaamgo/helper/global-logger';
import { getLocationHashWithoutModalPageFragment } from '../modal-fragment-helper';
import { User } from '../../../user';

export default {
    name: 'RegisterConfirmationEmailSentView',
    components: { ButtonWithLoadingState },
    mixins: [TransFilter, pageStoreMixin],
    mounted() {
        this.store.userPaymentModal.setSubmitting(false);
    },
    computed: {
        /** @return {Boolean} */
        isSubmitting() {
            return this.store.userPaymentModal.getSubmitting;
        },
        /** @return {User} */
        storeUser() {
            return this.store.user.getUser;
        },
        shouldShowEducational() {
            return (
                !projectHelper.isApi2Convert() &&
                !this.storeUser.hasRole(User.ROLE_USER_EDUCATIONAL) &&
                this.storeUser.hasRole(User.ROLE_USER_ELIGIBLE_FOR_EDUCATIONAL)
            );
        },
        titleText() {
            if (projectHelper.isApi2Convert() || !this.boughtSubscription) {
                return translate('Thank you for your purchase on %projectName%.', {
                    '%projectName%': projectHelper.getProjectName(),
                });
            }

            return translate('Thank you for subscribing');
        },
        mainText() {
            return translate(
                "We've sent you a verification link to your email address <strong>%email%</strong> to activate your account.",
                { '%email%': this.storeUser.getEmail() }
            );
        },
        educationText() {
            return this.translate(
                "Don't forget to confirm your email address in order to claim your <strong>free educational premium account.</strong>"
            );
        },
    },
    methods: {
        goToHelpPage() {
            window.allowLeave = true;
            window.location = satGlobals.getMainUrl() + translatePath('/help');
        },
        resendConfirmation() {
            if (this.disableResend) {
                return;
            }

            this.disableResend = true;
            globalLogger.log('user modal - confirmation email - resend - start');

            userManager
                .resendConfirmationMail(this.storeUser.getEmail())
                .then(() => {
                    globalLogger.log('user modal - confirmation email - resend - success');

                    const message = translate('A confirmation email was sent to <strong>%1</strong>', {
                        '%1': this.storeUser.getEmail(),
                    });

                    pageNotification.info(message);
                })
                .catch((error) => {
                    globalLogger.log('user modal - confirmation email - resend - fail', error);
                    pageNotification.showGenericErrorMessage();
                })
                .finally(() => {
                    // allow resending email with delay to prevent spam
                    setTimeout(() => {
                        this.disableResend = false;
                    }, 1000);
                });
        },
        goToDashboard() {
            window.allowLeave = true;
            window.location = satGlobals.getAccountUrl() + translatePath('/user/change-email');
        },
        doContinue() {
            this.store.userPaymentModal.close();
            // reload window to make sure user/js has correct state
            window.location.hash = getLocationHashWithoutModalPageFragment();

            window.allowLeave = true;
            window.location.reload();
        },
    },
    data() {
        return {
            disableResend: false,
        };
    },
};
</script>
